import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import cx from "classnames";
import { useLocalStorage } from "usehooks-ts";
import { logCustomEvent as brazeCustomEvent } from "@braze/web-sdk";
import {
  ErrorBoundaryProvider,
  PROFILE_EDIT_VIEW_LS_KEY,
} from "component-library";
import { BrazeCustomEvents } from "shared-services";
import { PROFILE_EDIT_VIEW_QUERY_PARAM } from "shared/constants";
import { checkIsCD, checkIsPerformer } from "shared-auth";
import ActionFooterSection from "pages/Profile/components/ActionFooterSection";
import CreditsSection from "pages/Profile/components/CreditsSection";
import Gallery from "pages/Profile/components/GallerySection";
import MediaSection from "pages/Profile/components/MediaSection";
import ProfileHeader from "pages/Profile/components/ProfileHeader";
import RepsSection from "pages/Profile/components/RepsSection";
import SkillsSection from "pages/Profile/components/SkillsSection";
import SocialMediaSection from "pages/Profile/components/SocialMediaSection";
import BasicInfoSection from "pages/Profile/components/BasicInfoSection";
import TrainingSection from "pages/Profile/components/TrainingSection";
import Footer from "pages/Profile/components/common/Footer";
import { useIsUserEditable } from "pages/Profile/hooks/useIsUserEditable";
import ProfilePrint from "pages/Profile/components/ProfilePrint";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useLayoutConfiguration } from "pages/Profile/hooks/useLayoutConfiguration";
import "pages/Profile/styles.scss";

const Profile = () => {
  const { headExpanded, bodyExpanded, expandHeadToggle, expandBodyToggle } =
    useLayoutConfiguration();
  const { isPublicView } = usePublicView();
  const isUserEditable = useIsUserEditable();
  const isCastingDirector = checkIsCD();
  const isPerformer = checkIsPerformer();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editQueryParam = searchParams.get(PROFILE_EDIT_VIEW_QUERY_PARAM);
  const [isEditStorageValue, setIsEdit] = useLocalStorage(
    PROFILE_EDIT_VIEW_LS_KEY,
    false
  );
  const isEdit = !isPublicView && isEditStorageValue;

  useEffect(() => {
    if (editQueryParam === "true" && isUserEditable) {
      setIsEdit(true);
    }

    if (location.search && editQueryParam) {
      navigate(
        {
          pathname: location.pathname,
          hash: location.hash,
        },
        { replace: true }
      );
    }
  }, [location, navigate, setIsEdit, editQueryParam, isUserEditable]);

  useEffect(() => {
    const isViewMode = isPublicView || !isEditStorageValue;

    try {
      if (isPerformer && isViewMode) {
        brazeCustomEvent(BrazeCustomEvents.PerformerOpenedViewProfile);
      }

      if (isPerformer && isEditStorageValue) {
        brazeCustomEvent(BrazeCustomEvents.PerformerOpenedEditProfile);
      }
    } catch (e) {
      // do nothing user unauthorized
    }
  }, []);

  const toggleEdit = () => {
    if (isUserEditable) {
      setIsEdit(!isEdit);
    }
  };

  const clickHeadToggle = () => {
    if (!document.startViewTransition) {
      expandHeadToggle();

      return;
    }

    document.startViewTransition(() => {
      expandHeadToggle();
    });
  };

  const clickBodyToggle = () => {
    if (!document.startViewTransition) {
      expandBodyToggle();

      return;
    }

    document.startViewTransition(() => {
      expandBodyToggle();
    });
  };

  return (
    <ErrorBoundaryProvider>
      <ProfilePrint />
      <div className="profile-page print:hidden">
        <div className="profile-page-container">
          <ProfileHeader isEdit={isEdit} toggleEdit={toggleEdit} />
          <div
            className={cx("profile-page-grid", {
              "head-expanded": headExpanded === true,
              "body-expanded": bodyExpanded === true,
            })}
          >
            <div className="grid-item--head">
              <Gallery
                isHeadExpanded={headExpanded}
                toggleHeadExpanded={clickHeadToggle}
                isEdit={isEdit}
              />
              <RepsSection isEdit={isEdit} />
              <SocialMediaSection isEdit={isEdit} />
              <BasicInfoSection isEdit={isEdit} />
            </div>
            <div className="grid-item--body relative">
              <MediaSection
                isEdit={isEdit}
                clickBodyToggle={clickBodyToggle}
                bodyExpanded={bodyExpanded}
              />
              <CreditsSection isEdit={isEdit} />
            </div>
            <div className="grid-item--tail">
              <SkillsSection isEdit={isEdit} />
              <TrainingSection isEdit={isEdit} />
            </div>
          </div>
          <Footer />
          {isCastingDirector && <ActionFooterSection />}
        </div>
      </div>
    </ErrorBoundaryProvider>
  );
};

export default Profile;
